/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import transloadit from 'js/lib/transloadit';

import { Button } from '@coursera/cds-core';

import type { AssemblyStatus } from 'bundles/account-profile/components/common/transloadit';

import _t from 'i18n!nls/account-profile';

export type UploadButtonProps = {
  label: string;
  onUpload: () => void;
  onProgress: (progress: number) => void;
  onSuccess: (result: AssemblyStatus) => void;
  onError: (errorMessage: string) => void;
  onReset?: () => void;
  disabled?: boolean;
  render?: (onClick: () => void) => JSX.Element;
  templateId: string;
  accept: string;
  maxSize: number;
  fields?: Record<string, unknown>;
};

const TRANSLOADIT_AUTH_KEY = '05912e90e83346abb96c261bf458b615';

class UploadButton extends React.Component<UploadButtonProps> {
  inputRef: HTMLInputElement | null = null;

  formRef: HTMLSpanElement | null = null;

  // Use ref for transloadid instance id doesn't affect the view hence we don't need to keep it in state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transloaditRef: React.MutableRefObject<any>;

  constructor(props: UploadButtonProps) {
    super(props);

    this.transloaditRef = React.createRef();
  }

  componentDidMount() {
    this.initForm();
  }

  componentWillUnmount() {
    this.resetUploader();
  }

  onUpload = () => {
    this.props.onUpload();
  };

  onProgress = (assembly: AssemblyStatus) => {
    const progress = (assembly.bytes_received / assembly.bytes_expected) * 100;
    if (!isNaN(progress)) {
      this.props.onProgress(progress);
    }
  };

  onSuccess = (assembly: AssemblyStatus) => {
    this.props.onSuccess(assembly);
    this.removeForm();
  };

  onError = (error: { assembly: { message: string } }) => {
    this.props.onError(error?.assembly?.message || _t('an error has occurred'));
    this.removeForm();
  };

  initForm = () => {
    const uploader = transloadit(this.formRef, {
      params: {
        // eslint-disable-next-line camelcase
        max_size: this.props.maxSize,
        auth: {
          key: TRANSLOADIT_AUTH_KEY,
        },
        // eslint-disable-next-line camelcase
        template_id: this.props.templateId,
        fields: this.props.fields,
      },
    });

    uploader.on('select', this.onUpload);
    uploader.on('progress', this.onProgress);
    uploader.on('success', this.onSuccess);
    uploader.on('error', this.onError);

    this.transloaditRef.current = uploader;
  };

  resetUploader = () => {
    const uploader = this.transloaditRef.current;
    uploader?.reset();
    uploader?.off('select', this.onUpload);
    uploader?.off('progress', this.onProgress);
    uploader?.off('success', this.onSuccess);
    uploader?.off('error', this.onError);
  };

  removeForm = () => {
    this.resetUploader();
    this.props.onReset?.();
  };

  onButtonClick = () => {
    this.inputRef?.click();
  };

  render() {
    const { label, render } = this.props;

    // transloadit will append an form element adjacent to the referenced element
    // So need the outer parent element to contain the appended form
    return (
      <div>
        {render ? (
          render(this.onButtonClick)
        ) : (
          <Button variant="primary" size="small" onClick={this.onButtonClick}>
            {label}
          </Button>
        )}

        <span
          className="transloadit-form"
          ref={(ref) => {
            this.formRef = ref;
          }}
        >
          <input
            id="file-uploader"
            type="file"
            name="my_file"
            accept={this.props.accept}
            ref={(ref) => {
              this.inputRef = ref;
            }}
          />
        </span>
      </div>
    );
  }
}

// we need to remount the component in order to pick up the new ref
const UploadButtonContainer = (props: UploadButtonProps) => {
  const [key, setKey] = useState(1);

  /**
   * Change a key to re-mount the component with transloadit transloadit requires re-initialization to function properly
   * @see https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
   */
  const handleReset = () => {
    setKey((prev) => prev + 1);
  };

  return (
    <div className="rc-UploadButtonContainer">
      <UploadButton key={key} {...props} onReset={handleReset} />
    </div>
  );
};

export default UploadButtonContainer;
